import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setCurrentStep,
  setObjectId,
  setFileName,
  parseCommissionFile,
  clearParseError,
} from "../../../redux/Commission/commissionStatementSlice";
import apiServices from "../../../service";

const NAVY = "#205393";
const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const GRN = "#2b8a3e";
const GRNL = "#e6f9f0";
const RED = "#c92a2a";
const ACC = "#1e4a8a";
const ACCL = "#e8f0fb";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const SUR2 = "#f0f0f0";

const ACCEPTED = [".xlsx", ".xls", ".csv"];
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

// Known-safe MIME types. Empty string and application/octet-stream are allowed through
// (some browsers don't report a specific type for CSV/XLS — extension check is the first gate).
const ALLOWED_MIME = new Set([
  "",
  "application/octet-stream",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/excel",
  "application/x-excel",
  "text/csv",
  "text/plain",
  "application/csv",
  "text/comma-separated-values",
]);

const StepUploadFile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const { providerId, providerName, startDate, endDate, receiptDate, loading, error } =
    useSelector((s: RootState) => s.commissionStatement);
  const userId = useSelector((s: RootState) => s.authUser?.userId ?? "");

  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "scanning" | "clean" | "error">("idle");
  const [localObjectId, setLocalObjectId] = useState<string | null>(null);
  const [localFileName, setLocalFileName] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [dupError, setDupError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = async (file: File) => {
    setDupError(null);
    setScanError(null);
    dispatch(clearParseError());
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED.includes(ext)) {
      setScanError(`Unsupported file type. Accepted: ${ACCEPTED.join(", ")}`);
      return;
    }

    if (!ALLOWED_MIME.has(file.type)) {
      setScanError(`Unsupported file type. Accepted: ${ACCEPTED.join(", ")}`);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setScanError(`File too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    // Duplicate file check — ask backend before upload/scan (covers all sessions, any status)
    try {
      const checkRes = await apiServices.commissionStatement.getAllStatements(1, 0, undefined, file.name);
      if ((checkRes.data?.total ?? 0) > 0) {
        setDupError(
          `"${file.name}" has already been uploaded for this provider and date range. ` +
          `The same file cannot be uploaded again. Please use a different file.`
        );
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
    } catch {
      // If the check itself fails, let the upload proceed — backend will enforce on parse
    }

    setDupError(null);
    setScanError(null);
    setUploadStatus("uploading");
    setLocalFileName(file.name);
    abortRef.current = new AbortController();

    try {
      const uploadRes = await apiServices.FileUpload.upload(file, userId, abortRef.current.signal);
      const objectId = uploadRes.data?.data?.[0]?.objectId || uploadRes.data?.objectId;
      if (!objectId) throw new Error("Upload did not return an objectId");

      setLocalObjectId(objectId);
      setUploadStatus("scanning");

      let attempts = 0;
      while (attempts < 60) {
        await new Promise((r) => setTimeout(r, 3000));
        const scanRes = await apiServices.FileUpload.scanStatus(objectId, abortRef.current.signal);
        const scanResult = scanRes.data?.data?.[0];
        const scanStatus = scanResult?.scanStatus;
        if (scanStatus === "SUCCESS") {
          setUploadStatus("clean");
          dispatch(setObjectId(objectId));
          dispatch(setFileName(file.name));
          return;
        }
        if (scanStatus === "UNSUPPORTED") {
          setScanError("This file is password-protected or encrypted and cannot be uploaded. Please remove the password and try again.");
          setUploadStatus("error");
          return;
        }
        if (scanStatus === "FAILED" || scanStatus === "ERROR" || scanResult?.isError) {
          setScanError("File failed virus scan. Please use a different file.");
          setUploadStatus("error");
          return;
        }
        attempts++;
      }
      setScanError("Scan timed out. Please try again.");
      setUploadStatus("error");
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setScanError(err?.message || "Upload failed");
        setUploadStatus("error");
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleClear = () => {
    abortRef.current?.abort();
    setUploadStatus("idle");
    setLocalObjectId(null);
    setLocalFileName(null);
    setScanError(null);
    setDupError(null);
    dispatch(clearParseError());
    dispatch(setObjectId(null));
    dispatch(setFileName(null));
  };

  const handleNext = async () => {
    if (!localObjectId || !localFileName) return;
    const result = await dispatch(
      parseCommissionFile({
        objectId: localObjectId,
        providerId,
        providerName,
        startDate,
        endDate,
        receiptDate,
        fileName: localFileName,
      })
    );
    if (parseCommissionFile.fulfilled.match(result)) {
      dispatch(setCurrentStep(2));
    } else {
      setUploadStatus("error");
    }
  };

  const clean = uploadStatus === "clean";

  const uzStyle: React.CSSProperties = {
    border: `2px dashed ${isDragging ? ACC : clean ? GRN : BRD2}`,
    borderStyle: clean ? "solid" : "dashed",
    borderRadius: "5px",
    background: isDragging ? ACCL : clean ? GRNL : SUR2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px 28px",
    cursor: "pointer",
    transition: "all 0.2s",
    textAlign: "center",
  };

  return (
    <div>
      <div style={{ background: "#fff", border: `1px solid ${BRD}`, borderRadius: "5px", padding: "14px 16px" }}>
        <div style={{ fontSize: "13px", fontWeight: 600, color: T1, marginBottom: "12px" }}>
          Step 2 — Upload Commission File
        </div>

        {/* Upload zone */}
        {(uploadStatus === "idle" || uploadStatus === "error") && (
          <div
            style={uzStyle}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.6">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <div style={{ fontSize: "13px", fontWeight: 500, color: T2, marginTop: "6px" }}>
              Drag &amp; drop your commission file here
            </div>
            <div style={{ fontSize: "11.5px", color: T3, marginTop: "2px" }}>
              .xlsx &nbsp;·&nbsp; .xls &nbsp;·&nbsp; .csv &nbsp;·&nbsp; Click to browse
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
          </div>
        )}

        {(uploadStatus === "uploading" || uploadStatus === "scanning") && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "20px", justifyContent: "center" }}>
            <Spinner size="small" />
            <span style={{ fontSize: "13px", color: T2 }}>
              {uploadStatus === "uploading" ? "Uploading file..." : "Scanning file for viruses..."}
            </span>
          </div>
        )}

        {uploadStatus === "clean" && localFileName && (
          <div style={uzStyle}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={GRN} strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <div style={{ fontSize: "12.5px", color: GRN, fontWeight: 600, marginTop: "4px" }}>
              {localFileName}
            </div>
            <div style={{ fontSize: "11.5px", color: T3, marginTop: "2px" }}>File ready</div>
          </div>
        )}

        {uploadStatus === "clean" && localFileName && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
            <span style={{ fontSize: "12px", color: T2 }}>{localFileName}</span>
            <button
              onClick={handleClear}
              style={{
                background: "#fff",
                color: RED,
                border: `1px solid ${RED}`,
                borderRadius: "5px",
                fontSize: "12px",
                fontWeight: 500,
                padding: "0 11px",
                height: "28px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              Clear File
            </button>
          </div>
        )}

        {dupError && (
          <div style={{
            background: "#fff8e6", border: "1px solid #e8a000", borderRadius: "5px",
            padding: "10px 14px", fontSize: "12.5px", color: "#7a4f00", marginTop: "10px",
            display: "flex", alignItems: "flex-start", gap: "8px",
          }}>
            <span style={{ fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>⚠</span>
            <span style={{ flex: 1 }}>{dupError}</span>
            <button
              onClick={() => setDupError(null)}
              style={{ background: "none", border: "none", color: "#7a4f00", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}
        {scanError && (
          <div style={{ fontSize: "12.5px", color: RED, marginTop: "8px" }}>{scanError}</div>
        )}
        {error.parse && (
          <div style={{ background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: "5px", padding: "8px 12px", fontSize: "12.5px", color: RED, marginTop: "8px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span style={{ flex: 1 }}>{error.parse}</span>
            <button
              onClick={() => dispatch(clearParseError())}
              style={{ background: "none", border: "none", color: RED, cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        {/* Button row */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${BRD}` }}>
          <button
            onClick={() => dispatch(setCurrentStep(0))}
            style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: "pointer" }}
          >
            ← Back
          </button>

          <div style={{ flex: 1 }} />

          <button
            onClick={handleNext}
            disabled={!clean || loading.parse}
            style={{
              background: clean && !loading.parse ? NAVY : "#c8cdd5",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              fontSize: "12.5px",
              fontWeight: 500,
              padding: "0 16px",
              height: "32px",
              cursor: clean && !loading.parse ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {loading.parse ? <Spinner size="tiny" /> : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepUploadFile;
